import React, { useState } from 'react';
import Grid from '@mui/material/Grid';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Button from '@mui/material/Button';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { formatQueryError } from 'renderer/shared/formatters/formatters';
import { useAppSelector } from 'renderer/shared/state/hooks';
import { selectUser } from 'renderer/features/auth/state/userSelectors';
import AddCardDialog from './AddCardDialog';
import DeleteCardDialog from './DeleteCardDialog';
import PaymentMethodCard from './PaymentMethodCard';
import { useListPaymentMethodsQuery } from '../services/paymentMethods.endpoints';
import { PaymentMethod } from '../schemas/paymentMethods.schemas';

const BillingTab: React.FC = () => {
  const { data: cards, isLoading, error } = useListPaymentMethodsQuery('');
  const user = useAppSelector(selectUser);
  const userId = user.role === 'public' ? '' : user._id;
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [paymentMethodFormOpen, setPaymentMethodFormOpen] = useState(false);
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  const [provider, setProvider] = useState<'stripe' | 'braintree'>('stripe');
  const [selectedCard, setSelectedCard] = useState<PaymentMethod | undefined>();

  const handleDeleteClick = (card: PaymentMethod) => {
    setSelectedCard(card);
    setDeleteDialogOpen(true);
  };

  const stripeCards = cards?.filter((card: any) => card.stripeId);
  const braintreeCards = cards?.filter((card: any) => card.braintreeId);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: 'bold', marginBottom: 5 }}>
        Payment Methods
      </Typography>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        <Grid container spacing={4} maxWidth={1200}>
          {/* Stripe / Subscriptions Column */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ mr: 1 }}>
                Subscriptions
              </Typography>

              <Button
                variant="outlined"
                color="primary"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => {
                  setProvider('stripe');
                  setPaymentMethodFormOpen(true);
                  setMode('create');
                  setSelectedCard(undefined);
                }}
              >
                Add Card
              </Button>
            </Box>

            {isLoading && (
              <Box display="flex" justifyContent="center" my={2}>
                <CircularProgress />
              </Box>
            )}
            {error && (
              <Alert severity="error" sx={{ textAlign: 'center' }}>
                <Typography>{formatQueryError(error)}</Typography>
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {stripeCards &&
                stripeCards.map((card) => (
                  <>
                    <PaymentMethodCard
                      key={card._id}
                      card={card}
                      onEditIconClick={() => {
                        setSelectedCard(card);
                        setMode('edit');
                        setProvider('stripe');
                        setPaymentMethodFormOpen(true);
                      }}
                      onDeleteIconClick={() => handleDeleteClick(card)}
                    />
                    <br />
                  </>
                ))}
            </Box>
          </Grid>

          {/* Braintree / Plugins Column */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ mr: 1 }}>
                Other Payment Methods (Plugins)
              </Typography>

              <Button
                variant="outlined"
                color="primary"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => {
                  setProvider('braintree');
                  setPaymentMethodFormOpen(true);
                  setMode('create');
                  setSelectedCard(undefined);
                }}
              >
                Add Card
              </Button>
            </Box>

            <Box
              sx={{
                width: '100%',
                maxWidth: 400,
                pl: { md: 4 },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  // mt: 2,
                }}
              >
                {braintreeCards &&
                  braintreeCards.map((card) => (
                    <>
                      <PaymentMethodCard
                        key={card._id}
                        card={card}
                        onEditIconClick={() => {
                          setSelectedCard(card);
                          setMode('edit');
                          setProvider('braintree');
                          setPaymentMethodFormOpen(true);
                        }}
                        onDeleteIconClick={() => handleDeleteClick(card)}
                      />
                      <br />
                    </>
                  ))}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Add / Edit Dialog */}
      <AddCardDialog
        open={paymentMethodFormOpen}
        onClose={() => setPaymentMethodFormOpen(false)}
        mode={mode}
        userId={userId}
        card={selectedCard}
        provider={provider}
      />

      <DeleteCardDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        card={selectedCard}
      />
    </Box>
  );
};

export default BillingTab;
